import { MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Spinner, StandAloneLink } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { AEMContentProvider, AemFeature, createAemUrl } from '@app/components/AdobeExperienceFragments';
import { selectConfig, selectLocale, selectUserInfo } from '@app/redux';

import { useShopRedirectIfNoSelection } from '../../hooks/use-redirect-to-shop';
import AddonAemUnavailable from '../AddonAemUnavailable/addon-aem-unavailable';
import Footer from '../Footer';
import EasyCareTerms from './easy-care-terms';

import styles from '../../styles/grouped-addon.module.scss';

const AEMFragment = ({
  handleBack,
  handlePrimaryAction,
  continueButtonLabel,
  disableAction,
  showEasyCareDisclaimers,
}: {
  handleBack: () => void;
  handlePrimaryAction: () => void;
  continueButtonLabel: string;
  disableAction: boolean;
  showEasyCareDisclaimers: boolean;
}) => {
  const { t } = useTranslation('ShopAddons');
  const [aemFragmentLoading, setAEMFragmentLoading] = useState(true);
  const [aemFragmentError, setAEMFragmentError] = useState(false);

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    userInfo: { accountType },
  } = useSelector(selectUserInfo);

  const { aemBaseUrl } = useSelector(selectConfig);

  const disclaimerAEM = createAemUrl({
    baseUrl: aemBaseUrl,
    locale: userLocale,
    feature: AemFeature.DISCLAIMER_EASY_CARE,
    accountType,
  });

  const videoAndBlurbAEM = createAemUrl({
    baseUrl: aemBaseUrl,
    locale: userLocale,
    feature: AemFeature.GROUPED_EASY_CARE,
    accountType,
  });

  useShopRedirectIfNoSelection();
  return (
    <>
      <div className={styles['aem-fragment']}>
        {aemFragmentLoading && (
          <div style={{ padding: '32px' }}>
            <Spinner label={t('cart.aemFragment.loading')} />
          </div>
        )}
        {aemFragmentError && !aemFragmentLoading ? (
          <div className={styles['addon-unavailable-wrapper']}>
            <AddonAemUnavailable />
          </div>
        ) : (
          <AEMContentProvider
            id={styles['aem-fragment-container']}
            style={{ display: aemFragmentLoading ? 'none' : 'block' }}
            src={disclaimerAEM}
            onLoad={() => setAEMFragmentLoading(false)}
            onError={() => {
              setAEMFragmentError(true);
            }}
          />
        )}
      </div>
      <div style={{ display: aemFragmentLoading ? 'none' : 'block' }} className={styles['card-footer']}>
        <EasyCareTerms />
        <Footer>
          <StandAloneLink
            variant={'primary'}
            linkSize={'medium'}
            href={'#'}
            data-cy="navigatorBackLink"
            onClick={(e: MouseEvent<HTMLElement>) => {
              handleBack();
              e.preventDefault();
            }}
          >
            {t('goBack')}
          </StandAloneLink>
          <Button variant="primary" onClick={handlePrimaryAction} buttonSize="large" disabled={disableAction}>
            {continueButtonLabel}
          </Button>
        </Footer>
      </div>
      {showEasyCareDisclaimers && (
        <>
          <hr className={styles['hr']} />
          <AEMContentProvider
            id={styles['aem-fragment-container']}
            style={{ display: aemFragmentLoading ? 'none' : 'block' }}
            src={videoAndBlurbAEM}
            onLoad={() => setAEMFragmentLoading(false)}
          />
        </>
      )}
    </>
  );
};
export default AEMFragment;
